import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { getDestination, getScholarships } from "../lib/api";
import { useSEO } from "../lib/useSEO";
import { useConsultation } from "../components/Consultation";
import SectionHeader from "../components/SectionHeader";
import CTASection from "../components/CTASection";
import Icon from "../components/Icon";
import { ScholarshipCard } from "../components/Cards";
import { Breadcrumbs } from "../components/layout/PageHero";
import { Reveal, Dots } from "../components/motion/Reveal";

const WHY_ICONS = ["GraduationCap", "Landmark", "Users", "Compass", "Globe2", "Sparkles"];
const DOT_COLORS = ["#E63946", "#2A9D8F", "#4EA8DE", "#E9C46A"];

export default function DestinationDetail() {
  const { slug } = useParams();
  const openConsultation = useConsultation();
  const [d, setD] = useState(null);
  const [scholarships, setScholarships] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useSEO(
    d ? `${d.tagline} — Suvera Academy` : "Study Destination — Suvera Academy",
    d ? d.description : "Explore study destinations with Suvera Academy."
  );

  useEffect(() => {
    setLoading(true);
    setNotFound(false);
    getDestination(slug)
      .then((dest) => {
        setD(dest);
        return getScholarships().then((s) =>
          setScholarships(s.filter((x) => x.country.toLowerCase() === dest.name.toLowerCase()))
        );
      })
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
  }, [slug]);

  if (loading) {
    return (
      <main className="min-h-screen pt-32" data-testid="destination-loading">
        <div className="mx-auto max-w-[1440px] px-4 sm:px-6 lg:px-10">
          <div className="h-[40vh] animate-pulse rounded-2xl bg-slate-200/70" />
        </div>
      </main>
    );
  }

  if (notFound || !d) {
    return (
      <main className="hero-spotlight flex min-h-screen items-center" data-testid="destination-not-found">
        <div className="mx-auto max-w-xl px-6 text-center">
          <Dots className="mb-6 justify-center" />
          <h1 className="font-display text-4xl font-semibold text-white">Destination Not Found</h1>
          <p className="mt-4 text-white/60">This destination page does not exist yet. Explore our current destinations instead.</p>
          <Link to="/destinations" className="mt-8 inline-flex rounded-full bg-white px-7 py-3.5 text-sm font-semibold text-navy" data-testid="destination-not-found-back">
            View All Destinations
          </Link>
        </div>
      </main>
    );
  }

  const theName = ["USA", "UK"].includes(d.name) ? `the ${d.name}` : d.name;

  return (
    <main data-testid="destination-detail-page">
      {/* Hero */}
      <section className="relative flex min-h-[78svh] items-end overflow-hidden" data-testid="destination-hero">
        <motion.img
          initial={{ scale: 1.08 }}
          animate={{ scale: 1 }}
          transition={{ duration: 1.4, ease: [0.22, 1, 0.36, 1] }}
          src={d.image}
          alt={d.tagline}
          className="absolute inset-0 h-full w-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-navy-dark via-navy-dark/50 to-navy-dark/25" />
        <div className="relative mx-auto w-full max-w-[1440px] px-4 pb-16 pt-40 sm:px-6 lg:px-10">
          <Breadcrumbs items={[{ label: "Home", to: "/" }, { label: "Destinations", to: "/destinations" }, { label: d.name }]} dark />
          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.15 }} className="max-w-3xl">
            <h1 className="font-display text-4xl font-semibold tracking-tight text-white sm:text-5xl lg:text-6xl" data-testid="destination-title">{d.tagline}</h1>
            {d.heroLine && (
              <p className="mt-3 font-display text-xl font-medium text-dot-amber sm:text-2xl" data-testid="destination-hero-line">{d.heroLine}</p>
            )}
            <button
              onClick={openConsultation}
              className="mt-8 inline-flex items-center gap-2 rounded-full bg-white px-7 py-3.5 text-sm font-semibold text-navy transition-colors duration-200 hover:bg-dot-amber hover:text-navy-dark"
              data-testid="destination-consultation-button"
            >
              Book a Free Consultation
              <Icon name="ArrowRight" className="h-4 w-4" />
            </button>
          </motion.div>
        </div>
      </section>

      {/* Overview */}
      <section className="mx-auto max-w-[1440px] px-4 py-16 sm:px-6 lg:px-10 lg:py-24" data-testid="destination-overview">
        <div className="grid grid-cols-1 gap-10 lg:grid-cols-12">
          <div className="lg:col-span-4">
            <SectionHeader chapter="Chapter 01" eyebrow="Overview" title={`Studying in ${theName}`} />
          </div>
          <Reveal className="lg:col-span-8">
            <p className="text-base leading-relaxed text-slate-600 sm:text-lg" data-testid="destination-intro">{d.intro}</p>
          </Reveal>
        </div>
      </section>

      {/* Why study here */}
      <section className="bg-sand">
        <div className="mx-auto max-w-[1440px] px-4 py-16 sm:px-6 lg:px-10 lg:py-24" data-testid="destination-why">
          <SectionHeader chapter="Chapter 02" eyebrow={`Why Study in ${theName}`} title={d.whyHeadline} />
          <div className={`grid grid-cols-1 gap-6 sm:grid-cols-2 ${d.whyStudy.length > 4 ? "lg:grid-cols-5" : "lg:grid-cols-4"}`}>
            {d.whyStudy.map((w, i) => (
              <Reveal key={w.title} delay={(i % 5) * 0.07} className="h-full">
                <div className="h-full rounded-2xl border border-slate-200 bg-white p-6 transition-[transform,box-shadow] duration-300 hover:-translate-y-1.5 hover:shadow-[0_20px_50px_-16px_rgba(13,24,51,0.18)]" data-testid={`why-card-${i}`}>
                  <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-navy/5 text-navy">
                    <Icon name={WHY_ICONS[i % WHY_ICONS.length]} className="h-5 w-5" />
                  </span>
                  <h3 className="mt-4 font-display text-lg font-semibold leading-snug text-navy-dark">{w.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-slate-600">{w.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Popular programs */}
      <section className="mx-auto max-w-[1440px] px-4 py-16 sm:px-6 lg:px-10 lg:py-24" data-testid="destination-programs">
        <SectionHeader chapter="Chapter 03" eyebrow="Popular Programs" title={d.programsHeadline} />
        <div className="grid grid-cols-1 gap-x-12 gap-y-8 md:grid-cols-2">
          {d.popularPrograms.map((p, i) => (
            <Reveal key={p.title} delay={(i % 2) * 0.07}>
              <div className="flex gap-4 border-b border-slate-100 pb-7" data-testid={`program-row-${i}`}>
                <span className="mt-2 h-2.5 w-2.5 shrink-0 rounded-full" style={{ backgroundColor: DOT_COLORS[i % 4] }} />
                <div>
                  <h3 className="font-display text-xl font-semibold text-navy-dark">{p.title}</h3>
                  <p className="mt-1.5 text-sm leading-relaxed text-slate-600">{p.text}</p>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* Work & stay-back (where applicable) */}
      {d.workInfo && (
        <section className="bg-sand" data-testid="destination-work-info">
          <div className="mx-auto max-w-[1440px] px-4 py-16 sm:px-6 lg:px-10 lg:py-24">
            <SectionHeader chapter="Work Rights" eyebrow="Part-Time Work & Stay-Back Options" title={d.workInfo.headline} description={d.workInfo.intro} />
            <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
              <Reveal>
                <div className="h-full rounded-2xl bg-navy p-8 text-white sm:p-10">
                  <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-white/10"><Icon name="Clock" className="h-6 w-6" /></span>
                  <h3 className="mt-5 font-display text-2xl font-semibold">Part-Time Work</h3>
                  <ul className="mt-4 space-y-3">
                    {d.workInfo.partTime.map((x) => (
                      <li key={x} className="flex items-start gap-2.5 text-sm leading-relaxed text-white/80">
                        <Icon name="CheckCircle2" className="mt-0.5 h-4 w-4 shrink-0 text-dot-amber" />
                        {x}
                      </li>
                    ))}
                  </ul>
                </div>
              </Reveal>
              <Reveal delay={0.1}>
                <div className="h-full rounded-2xl border border-slate-200 bg-white p-8 sm:p-10">
                  <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-navy/5 text-navy"><Icon name="Route" className="h-6 w-6" /></span>
                  <h3 className="mt-5 font-display text-2xl font-semibold text-navy-dark">Stay-Back Options</h3>
                  <ul className="mt-4 space-y-3">
                    {d.workInfo.stayBack.map((x) => (
                      <li key={x} className="flex items-start gap-2.5 text-sm leading-relaxed text-slate-700">
                        <Icon name="CheckCircle2" className="mt-0.5 h-4 w-4 shrink-0 text-dot-green" />
                        {x}
                      </li>
                    ))}
                  </ul>
                </div>
              </Reveal>
            </div>
          </div>
        </section>
      )}

      {/* Visa & application */}
      <section className="hero-spotlight" data-testid="destination-visa">
        <div className="mx-auto max-w-[1440px] px-4 py-16 sm:px-6 lg:px-10 lg:py-24">
          <SectionHeader dark chapter="Chapter 04" eyebrow="Visa & Application" title={d.visa.headline} />
          <div className="grid grid-cols-1 gap-6 lg:grid-cols-12">
            <Reveal className="lg:col-span-7">
              <div className="h-full rounded-2xl border border-white/10 bg-white/5 p-8 backdrop-blur-sm sm:p-10">
                <h3 className="flex items-center gap-2.5 font-mono-accent text-xs uppercase tracking-[0.25em] text-dot-amber">
                  <Icon name="Stamp" className="h-4 w-4" /> Eligibility Requirements
                </h3>
                <ul className="mt-6 space-y-3.5">
                  {d.visa.eligibility.map((e) => (
                    <li key={e} className="flex items-start gap-3 text-sm leading-relaxed text-white/80 sm:text-base">
                      <Icon name="CheckCircle2" className="mt-0.5 h-4 w-4 shrink-0 text-dot-green" />
                      {e}
                    </li>
                  ))}
                </ul>
              </div>
            </Reveal>
            <div className="space-y-6 lg:col-span-5">
              <Reveal delay={0.08}>
                <div className="rounded-2xl bg-white p-7" data-testid="visa-type-card">
                  <p className="font-mono-accent text-[10px] uppercase tracking-[0.22em] text-slate-400">Visa Type</p>
                  <p className="mt-2 font-display text-xl font-semibold text-navy-dark">{d.visa.type}</p>
                </div>
              </Reveal>
              {d.visa.intakes.length > 0 && (
                <Reveal delay={0.14}>
                  <div className="rounded-2xl border border-white/10 bg-white/5 p-7 backdrop-blur-sm" data-testid="visa-intakes-card">
                    <p className="font-mono-accent text-[10px] uppercase tracking-[0.22em] text-white/40">Application Timeline</p>
                    <div className="mt-4 space-y-4">
                      {d.visa.intakes.map((it) => (
                        <div key={it.name} className="flex gap-3.5">
                          <span className="mt-1.5 h-2 w-2 shrink-0 rounded-full bg-dot-amber" />
                          <div>
                            <p className="text-sm font-semibold text-white">{it.name}</p>
                            <p className="text-sm text-white/60">{it.detail}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </Reveal>
              )}
              <Reveal delay={0.2}>
                <div className="rounded-2xl border border-dot-amber/30 bg-dot-amber/10 p-7" data-testid="visa-processing-card">
                  <p className="flex items-center gap-2 font-mono-accent text-[10px] uppercase tracking-[0.22em] text-dot-amber">
                    <Icon name="Clock" className="h-4 w-4" /> Processing Time
                  </p>
                  <p className="mt-2 text-sm leading-relaxed text-white/85">{d.visa.processingTime}</p>
                </div>
              </Reveal>
            </div>
          </div>
        </div>
      </section>

      {/* Academic requirements */}
      <section className="mx-auto max-w-[1440px] px-4 py-16 sm:px-6 lg:px-10 lg:py-24" data-testid="destination-requirements">
        <SectionHeader chapter="Chapter 05" eyebrow="Entry Requirements" title="Academic Requirements" />
        <div className={`grid grid-cols-1 gap-6 sm:grid-cols-2 ${d.academicRequirements.length > 2 ? "lg:grid-cols-3" : "lg:grid-cols-2"}`}>
          {d.academicRequirements.map((r, i) => (
            <Reveal key={r.level} delay={i * 0.07} className="h-full">
              <div className="h-full rounded-2xl border border-slate-200 bg-white p-7" data-testid={`requirement-card-${i}`}>
                <p className="font-mono-accent text-[10px] uppercase tracking-[0.22em] text-slate-400">{String(i + 1).padStart(2, "0")}</p>
                <h3 className="mt-2 font-display text-xl font-semibold text-navy-dark">{r.level}</h3>
                <ul className="mt-4 space-y-2.5">
                  {r.items.map((item) => (
                    <li key={item} className="flex items-start gap-2.5 text-sm leading-relaxed text-slate-600">
                      <Icon name="CheckCircle2" className="mt-0.5 h-3.5 w-3.5 shrink-0 text-dot-blue" />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            </Reveal>
          ))}
        </div>
        {d.academicNote && <p className="mt-6 text-xs italic text-slate-400">Note: {d.academicNote}</p>}
      </section>

      {/* Scholarships */}
      <section className="bg-sand">
        <div className="mx-auto max-w-[1440px] px-4 py-16 sm:px-6 lg:px-10 lg:py-24" data-testid="destination-scholarships">
          <SectionHeader
            chapter="Chapter 06"
            eyebrow="Scholarships"
            title={`Scholarships in ${theName}`}
            description="Scholarship availability changes by institution and intake. Below are indicative opportunities — an advisor can confirm what you are eligible for."
          />
          {scholarships.length ? (
            <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
              {scholarships.map((s, i) => (
                <Reveal key={s.id} delay={i * 0.08} className="h-full"><ScholarshipCard s={s} /></Reveal>
              ))}
            </div>
          ) : (
            <Reveal>
              <div className="rounded-2xl border border-dashed border-slate-300 bg-white p-10 text-center text-sm text-slate-500" data-testid="destination-scholarships-empty">
                Scholarship listings for {d.name} are being updated. Contact Suvera Academy for current opportunities.
              </div>
            </Reveal>
          )}
        </div>
      </section>

      <CTASection title={d.finalCta.headline} description={d.finalCta.text} />
    </main>
  );
}
